'use client';

import { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import Image from 'next/image';
import '@/styles/brutalist/modal.css';

type ModalState = 'login' | 'signup' | 'forgot-password';

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginModal({ open, onOpenChange }: LoginModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <LoginModalContent onOpenChange={onOpenChange} />}
    </Dialog>
  );
}

function LoginModalContent({ onOpenChange }: Pick<LoginModalProps, 'onOpenChange'>) {
  const [modalState, setModalState] = useState<ModalState>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isEmailNotConfirmed, setIsEmailNotConfirmed] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const router = useRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const supabase = createClient();

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        // Check if it's an email not confirmed error
        const isNotConfirmed = signInError.message.toLowerCase().includes('email not confirmed');
        setIsEmailNotConfirmed(isNotConfirmed);
        setError(signInError.message);
        setIsLoading(false);
        return;
      }

      if (!data.user) {
        setError('Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // Check if user has a forwarder profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, company_name')
        .eq('id', data.user.id)
        .maybeSingle<{ role: string; company_name: string | null }>();

      if (profileError) {
        console.error('[LoginModal] Profile fetch error:', profileError);
        setError(`Profile error: ${profileError.message}. Contact support.`);
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      if (!profile) {
        setError('No profile found. Contact support to complete your account setup.');
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      if (profile.role === 'admin') {
        setError('Please use the admin login portal.');
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      const defaultRedirect =
        profile.role === 'shipper' ? '/dashboard/my-shipments' : '/forwarders/dashboard';

      onOpenChange(false);
      setTimeout(() => {
        window.location.href = defaultRedirect;
      }, 100);
    } catch (err) {
      console.error('[LoginModal] Unexpected login error:', err);
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(`Login failed: ${message}`);
      setIsLoading(false);
    }
  };

  const handleSignup = () => {
    onOpenChange(false);
    router.push('/signup');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const supabase = createClient();

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
        setIsLoading(false);
        return;
      }

      setResetEmailSent(true);
      setIsLoading(false);
    } catch {
      setError('Failed to send reset email. Please try again.');
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setIsResending(true);
    setResendSuccess(false);
    setError(null);

    try {
      const response = await fetch('/api/auth/resend-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to resend confirmation email');
        setIsResending(false);
        return;
      }

      setResendSuccess(true);
      setIsEmailNotConfirmed(false);
      setIsResending(false);
    } catch {
      setError('Failed to resend confirmation email. Please try again.');
      setIsResending(false);
    }
  };

  return (
      <DialogContent className="brutalist-modal modal-content sm:max-w-[450px] border-2 border-[#1F1C1B] rounded-none">
        <div className="modal-header">
          <div className="modal-logo flex justify-center">
            <Image src="/logo.svg" alt="Shipzy" width={120} height={36} />
          </div>
          <DialogTitle className="modal-title">
            {modalState === 'login' && 'Welcome Back'}
            {modalState === 'signup' && 'Get Started'}
            {modalState === 'forgot-password' && 'Reset Password'}
          </DialogTitle>
          <DialogDescription className="modal-description">
            {modalState === 'login' && 'Sign in to access your forwarder dashboard'}
            {modalState === 'signup' && 'Apply to join Shipzy as a verified forwarder'}
            {modalState === 'forgot-password' && 'Enter your email to receive a password reset link'}
          </DialogDescription>
        </div>

        <div className="modal-body">
          {modalState === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="form-field">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                  disabled={isLoading}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <div className="label-row">
                  <label htmlFor="password" className="form-label">Password</label>
                  <button
                    type="button"
                    onClick={() => setModalState('forgot-password')}
                    className="forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                  className="form-input"
                />
              </div>

              {resendSuccess && (
                <div className="success-message" style={{ marginBottom: 'var(--spacing-md)' }}>
                  Confirmation email sent! Check your inbox and click the link to confirm your email.
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                  {isEmailNotConfirmed && (
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      disabled={isResending}
                      className="resend-button"
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Resending...
                        </>
                      ) : (
                        'Resend Confirmation Email'
                      )}
                    </button>
                  )}
                </div>
              )}

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

              <p className="modal-footer-text">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => setModalState('signup')}
                >
                  Sign up
                </button>
              </p>
            </form>
          )}

          {modalState === 'signup' && (
            <div>
              <p className="modal-description" style={{ marginBottom: 'var(--spacing-md)', textAlign: 'left' }}>
                To join Shipzy as a verified freight forwarder, you&apos;ll need to complete an application form.
              </p>
              <button onClick={handleSignup} className="submit-button">
                Go to Application Form
              </button>
              <p className="modal-footer-text">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setModalState('login')}
                >
                  Sign in
                </button>
              </p>
            </div>
          )}

          {modalState === 'forgot-password' && !resetEmailSent && (
            <form onSubmit={handleForgotPassword}>
              <div className="form-field">
                <label htmlFor="reset-email" className="form-label">Email</label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                  disabled={isLoading}
                  className="form-input"
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <p className="modal-footer-text">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => setModalState('login')}
                >
                  Sign in
                </button>
              </p>
            </form>
          )}

          {modalState === 'forgot-password' && resetEmailSent && (
            <div>
              <div className="success-message">
                Password reset email sent! Check your inbox and follow the link to reset your password.
              </div>
              <button onClick={() => setModalState('login')} className="submit-button">
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </DialogContent>
  );
}
